import React, { FC } from "react";
import { Response, SuggestionTypes } from "stentor-models";

import { SearchResponseBubble } from "../SearchResponseBubble";
import { SearchResponseDisplay } from "../ResponseDisplay";
import { SearchResponseOutputSpeech } from "../SearchResponseOutputSpeech";
import { SuggestedActions } from "../SuggestedActions";
import { isForm } from "../../utils/guards";
import { FormNextData, FormStepUpdateDisplay } from "../FormDisplay/model";

import { SearchResponsePart } from "./SearchResponsePart";

import styles from "./FormResponse.module.scss";

export interface FormResponseProps {
    readonly response: Response;
    readonly className?: string;
    readonly loading: boolean;
    onActionClick(action: SuggestionTypes): void;
    onActionResponse(data: unknown, action: string): void;
    onAudioChange(isOn: boolean): void;
    onTrackNextStep?: (stepName: string, stepIndex: number, stepData: Record<string, unknown>, isLastStep: boolean) => void;
    onTrackPreviousStep?: (fromStepName: string, fromStepIndex: number, toStepName: string, toStepIndex: number) => void;
    onTrackSubmit?: (stepName: string, stepIndex: number, completedData: Record<string, unknown>, totalSteps: number) => void;
    /**
     * Callback to track a FORM_SUBMIT dispatch that failed outright (issue #1373).
     */
    onTrackSubmitFailure?: (stepName: string, stepIndex: number, totalSteps: number, reason: string) => void;
    currentStepDataRef?: React.MutableRefObject<Record<string, unknown>>;
    onFormNext?: (data: FormNextData) => Promise<unknown>;
    stepUpdate?: FormStepUpdateDisplay;
    onTrackHandoffRendered?: (stepName: string, provider: string) => void;
    onTrackHandoffNoRender?: (stepName: string, provider: string) => void;
    onTrackHandoffScheduled?: (stepName: string, provider: string, payload?: unknown) => void;
    onTrackHandoffError?: (stepName: string, provider: string) => void;
}

/**
 * FormResponse displays the data from the handler
 */
export const FormResponse: FC<FormResponseProps> = (props) => {
    const {
        response,
        className,
        onActionClick,
        onActionResponse,
        onAudioChange,
        loading,
        onTrackNextStep,
        onTrackPreviousStep,
        onTrackSubmit,
        onTrackSubmitFailure,
        currentStepDataRef,
        onFormNext,
        stepUpdate,
        onTrackHandoffRendered,
        onTrackHandoffNoRender,
        onTrackHandoffScheduled,
        onTrackHandoffError,
    } = props;

    const { outputSpeech, displays } = response;
    const actions = typeof outputSpeech === "object" ? outputSpeech.suggestions : undefined;

    return (
        <div className={`${styles.xapp_search_response} ${className || ""}`}>
            {!!outputSpeech && (
                <SearchResponsePart>
                    <SearchResponseBubble>
                        <SearchResponseOutputSpeech
                            outputSpeech={outputSpeech}
                            onAudioChange={onAudioChange}
                        />
                    </SearchResponseBubble>
                </SearchResponsePart>
            )}

            {!!actions?.length && actions?.length > 0 && (
                <SuggestedActions actions={actions} onActionClick={onActionClick} />
            )}

            {!!displays?.length &&
                displays?.length > 0 &&
                displays.map((display, index) => {
                    const noHeaderForm: boolean =
                        isForm(display) && (!display.header || display.header.length === 0);

                    return (
                        <SearchResponsePart
                            key={index}
                            extraClass={noHeaderForm ? "noHeaderForm" : ""}>
                            <SearchResponseDisplay
                                display={display}
                                onActionResponse={onActionResponse}
                                loading={loading}
                                onTrackNextStep={onTrackNextStep}
                                onTrackPreviousStep={onTrackPreviousStep}
                                onTrackSubmit={onTrackSubmit}
                                onTrackSubmitFailure={onTrackSubmitFailure}
                                currentStepDataRef={currentStepDataRef}
                                onFormNext={onFormNext}
                                stepUpdate={stepUpdate}
                                onTrackHandoffRendered={onTrackHandoffRendered}
                                onTrackHandoffNoRender={onTrackHandoffNoRender}
                                onTrackHandoffScheduled={onTrackHandoffScheduled}
                                onTrackHandoffError={onTrackHandoffError}
                            />
                        </SearchResponsePart>
                    );
                })}
        </div>
    );
};
